<template>
  <v-row
    class="mx-0 mt-0 mb-4"
    v-if="templateConfig.theme !== 'Operator-Template'"
  >
    <v-col>
      <v-form lazy-validation @submit="verifyPin()" onSubmit="return false;">
        <Alert
          :message="error"
          type="error"
          v-if="showError"
          @toggleAlert="showError = !showError"
        />
        <Alert
          :message="success"
          type="success"
          v-if="showSuccess"
          @toggleAlert="showSuccess = !showSuccess"
        />

        <v-row>
          <v-col>
            <v-text-field
              ref="OTPInput"
              pt-0
              class="centered-input pinCodeInput"
              color="success"
              maxlength="6"
              :disabled="isPinCodeDisabled"
              v-model="pinCode"
              :rules="pincodeRules"
              :label="merchantConfig.texts.pinCodeTextFieldLabel"
              required
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <span class="caption codeSendToLbl">
              {{ $translate('CODESENTTO') }}
              {{ SubscribeFlowDetails.msisdn }}
            </span>
            <a @click="resendPin()" class="green--text caption ml-1 mr-1">
              {{ $translate('RESENDPIN') }}
            </a>
            <v-progress-circular
              class="mx-1"
              :width="1"
              :size="15"
              v-if="sendingPinCode"
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              elevation="0"
              block
              :disabled="!isButtonActive"
              :loading="loading"
              :rounded="templateConfig.isButtonRounded"
              class="mainButton"
              v-prevent-multiple-clicks:click="verifyPin"
            >
              <span v-html="pinCodeBtnText" v-if="!loading"></span>
            </v-btn>
          </v-col>
        </v-row>
        <SecondaryDisclaimer
          v-if="pinCodeSecondaryDisclaimerText || freeStartDisclaimerText"
        >
          <div class="secondryDisc">
            <div v-html="freeStartDisclaimerText"></div>
            <div v-html="pinCodeSecondaryDisclaimerText"></div>
          </div>
        </SecondaryDisclaimer>
      </v-form>
    </v-col>
  </v-row>

  <!-- embedded template-->
  <v-row
    class="mx-0 mt-0 mb-4"
    v-else-if="templateConfig.theme == 'Operator-Template'"
  >
    <v-col>
      <v-row class="justify-center">
        <v-col>
          <p>
            {{
              this.$translate(
                this.pinCodeLength
                  ? 'ENTER_PINCODEWITHLENGTH_MSG'
                  : 'ENTER_PINCODE_MSG'
              )
                .replace('{pincodeLength}', this.pinCodeLength)
                .replace('{mobileNumber}', this.SubscribeFlowDetails.msisdn)
            }}

            <a
              @click="changeNumber"
              class="secondry--text text-decoration-underline mx-1"
            >
              {{ this.$translate('ChangeNumber') }}
            </a>
          </p>
        </v-col>
      </v-row>
      <v-form lazy-validation @submit="verifyPin()" onSubmit="return false;">
        <v-row class="justify-center">
          <v-col cols="12" sm="7" md="5" lg="4" xl="3">
            <Alert
              :message="error"
              type="error"
              v-if="showError"
              @toggleAlert="showError = !showError"
            />
            <Alert
              :message="success"
              type="success"
              v-if="showSuccess"
              @toggleAlert="showSuccess = !showSuccess"
            />
          </v-col>
        </v-row>

        <v-row class="justify-center">
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-otp-input
              v-if="pinCodeLength"
              ref="OTPInput"
              class="OTPInput"
              :length="pinCodeLength"
              :disabled="isPinCodeDisabled"
              v-model="pinCode"
              :rules="pincodeRules"
              required
              type="number"
            ></v-otp-input>

            <v-text-field
              v-else
              ref="OTPInput"
              pt-0
              class="centered-input pinCodeInput"
              color="primary"
              maxlength="6"
              :disabled="isPinCodeDisabled"
              v-model="pinCode"
              :rules="pincodeRules"
              :label="merchantConfig.texts.pinCodeTextFieldLabel"
              required
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="justify-center">
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-btn
              elevation="0"
              block
              :disabled="!isButtonActive"
              :loading="loading"
              :rounded="templateConfig.isButtonRounded"
              class="mainButton"
              v-prevent-multiple-clicks:click="verifyPin"
            >
              <span v-html="pinCodeBtnText" v-if="!loading"></span>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col v-if="showResendPinCode">
            <span>
              {{ $translate('didnotReceiveCodeLbl') }}
            </span>
            <a
              @click="resendPin()"
              class="secondry--text mx-1"
              v-if="!sendingPinCode"
            >
              {{ $translate('RESENDPIN') }}
            </a>
            <v-progress-circular
              class="mx-1"
              :width="1"
              :size="15"
              v-if="sendingPinCode"
              indeterminate
              color="secondry"
            ></v-progress-circular>
          </v-col>

          <v-col v-else-if="showResendPinCodeTimer">
            <span>
              {{ $translate('ResendPinCodeIn') }}
              {{
                new Date(resendPinCodeTimer * 1000)
                  .toISOString()
                  .substring(14, 19)
              }}
            </span>
          </v-col>
        </v-row>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { digitsConverter } from '../../../helper/digitsHelper.js';
import pincodeRules from '../../Shared/Rules/pincodeRules';
import SecondaryDisclaimer from '../../Shared/components/Disclaimer.vue';
import regex from '../../../constants/regex';
import Alert from '../../Shared/components/Alert.vue';

const RESENDINTERVALTIME = 60;

export default {
  components: { SecondaryDisclaimer, Alert },
  computed: {
    ...mapState(['templateConfig', 'SubscribeFlowDetails', 'merchantConfig']),
    isButtonActive() {
      return this.pincodeRules[0](this.pinCode) === true;
    },
    pinCodeBtnText() {
      //Needs $replacePlaceholders plugin function for [productName, productPrice, currency] placeholders.
      return this.$replacePlaceholders(this.merchantConfig.texts.pinCodeBtn, {
        productName: this.SubscribeFlowDetails.initialProductName,
        price:
          this.SubscribeFlowDetails.selectedOperator.disclaimerParams.price,
        currency:
          this.SubscribeFlowDetails.selectedOperator.disclaimerParams.currency,
      });
    },
    pinCodeSecondaryDisclaimerText() {
      return this.merchantConfig.texts.pinCodeSecondaryDisclaimer;
    },

    freeStartDisclaimerText() {
      //Needs $replacePlaceholders plugin function for [productPrice, currency, frequency, trialPeriod] placeholders.
      //for verification page if freeStartPeriod: exist & UserShouldGetFreeStartPeriod = true >> show disclaimer
      if (
        this.SubscribeFlowDetails.freeStartPeriod &&
        this.SubscribeFlowDetails.userShouldGetFreeStartPeriod
      ) {
        return this.$replacePlaceholders(
          this.merchantConfig.texts.freeStartDisclaimer,
          {
            price:
              this.SubscribeFlowDetails.selectedOperator.disclaimerParams.price,
            currency:
              this.SubscribeFlowDetails.selectedOperator.disclaimerParams
                .currency,
            frequency: this.SubscribeFlowDetails.frequency,
            trialPeriod: this.SubscribeFlowDetails.freeTrialDays,
          }
        );
      } else {
        return '';
      }
    },

    pinCodeLength() {
      return this.SubscribeFlowDetails.selectedOperator.pinCodeLength;
    },
  },
  data() {
    return {
      sendingPinCode: false,
      showSuccess: false,
      showError: false,
      loading: false,
      error: '',
      success: '',
      pinCode: '',
      resendAttempts: 0,
      isPinCodeDisabled: false,
      pincodeRules: pincodeRules(this.$translate, regex.PINCODE),
      resendPinCodeTimer: RESENDINTERVALTIME,
      showResendPinCode: true,
      showResendPinCodeTimer: false,
      resendPinCodeInterval: null,
    };
  },
  deactivated() {
    // reset the component before leave
    this.pinCode = '';
    this.$refs.OTPInput.isResetting = true; // access data of the OTPInput component
    this.resetResendPinCodeTimer();
  },
  activated() {
    this.startResendPinCodeTimer();
  },
  methods: {
    ...mapMutations(['setSubscribeFlowDetails']),
    async verifyPin() {
      this.loading = true;
      this.isPinCodeDisabled = true;
      this.showError = false;
      this.showSuccess = false;

      this.pinCode = digitsConverter(this.pinCode);
      try {
        let response = await this.$http.post(`/sub/CompleteSubscription`, {
          PinCode: this.pinCode,
          simulate: process.env.NODE_ENV === 'development' ? true : false,
        });
        // let response = {
        //   data: {
        //     errorMessage: 'Failed To Charge User',
        //     merchantRedirectUrl: null,
        //     operationStatusCode: '51',
        //     paymentStatus: '4',
        //     responseCode: 4,
        //     statusCode: 0,
        //     subscriptionContractId: 43238,
        //   },
        // };

        if (response.data.errorMessage) {
          if (response.data.paymentStatus) {
            switch (Number(response.data.paymentStatus)) {
              case 4:
              case 14:
                this.moveToFailurePage(
                  this.$translate(
                    `RESPONSECODE_${response.data.paymentStatus}`
                  ),
                  response.data.operationStatusCode
                );
                break;
              case 9:
                this.moveToFailurePage(
                  this.$translate('GENERALERRORMESSAGE'),
                  response.data.operationStatusCode
                );
                break;
              case 11:
              case 16:
                this.showErrorMessage(
                  this.$translate(`RESPONSECODE_${response.data.paymentStatus}`)
                );
                break;
              default:
                this.moveToFailurePage(
                  this.$translate('GENERALERRORMESSAGE'),
                  response.data.operationStatusCode
                );
            }
          } else {
            switch (response.data.errorMessage) {
              case 'Invalid Pin Code': //New response errorMessage detected for the same API calling.
              case 'Invalid Pincode': //New response errorMessage detected for the same API calling.
              case 'InvalidPinCodeOrPassword':
              case 'invalidpin':
                this.showErrorMessage(this.$translate('INVALIDPINCODE'));
                break;
              case 'ExpiredPinCodeOrPassword':
                this.showErrorMessage(this.$translate('EXPIREDPINCODE'));
                break;
              case 'Merchant rejected the subscription':
                this.moveToFailurePage(
                  this.$translate('REJECTEDSUBSCRIPTION'),
                  response.data.operationStatusCode
                );
                break;
              case 'Failed to charge user':
              case 'Failed To Charge User':
                this.moveToFailurePage(
                  this.$translate('GENERALERRORMESSAGE'), //optional
                  response.data.operationStatusCode
                );
                break;
              case 'Exceed Max Attempt Reached Of Invalid Pin':
              case `You have exceeded maximum invalid PIN trials attempt. Please close the widget, refresh the provider's page then relaunch the T-Pay widget again.`:
                this.moveToFailurePage(
                  this.$translate('INVALIDTRIALSEXCEEDED'),
                  response.data.operationStatusCode
                );
                break;
              default:
                this.moveToFailurePage(
                  this.$translate('GENERALERRORMESSAGE'), //optional
                  '51'
                );
            }
          }
        } else {
          this.moveToSuccessPage(response.data.subscriptionContractId);
        }
      } catch (error) {
        this.showErrorMessage(this.$translate('GENERALERRORMESSAGE'));
      } finally {
        this.loading = false;
        this.isPinCodeDisabled = false;
      }
    },

    showErrorMessage(msg) {
      this.error = msg;
      this.showError = true;
    },

    moveToFailurePage(msg, operationStatusCode) {
      this.setSubscribeFlowDetails({
        step: 4,
        component: 'SubFailure',
        failureMessage: msg,
        operationStatusCode,
      });
    },

    moveToSuccessPage(id) {
      this.setSubscribeFlowDetails({
        step: 3,
        component: 'SubSuccess',
        paymentId: id,
      });
    },

    async resendPin() {
      this.resendAttempts++;
      if (this.resendAttempts > 4) {
        this.showSuccess = false;
        this.showErrorMessage(this.$translate('RESENDTRIALSEXCEEDED'));
      } else {
        this.sendingPinCode = true;
        let response = await this.$http.post('/sub/ResendPinCode', {
          transactionId: this.SubscribeFlowDetails.transactionId,
          simulate: process.env.NODE_ENV === 'development' ? true : false,
        });
        this.showError = false;
        this.showSuccess = false;
        if (response.data.errorMessage) {
          if (
            response.data.errorMessage == 'Verification SMS Not Sent' ||
            response.data.errorMessage == 'Failed To Send Verification SMS'
          ) {
            this.showErrorMessage(this.$translate('PINCODENOTSENT'));
          } else {
            this.moveToFailurePage(
              this.$translate('GENERALERRORMESSAGE'), //optional
              '51'
            );
          }
        } else {
          this.success = this.$translate('PINCODESENTSUCCESS');
          this.showSuccess = true;
        }
        this.sendingPinCode = false;
        this.startResendPinCodeTimer();
      }
    },

    changeNumber() {
      this.setSubscribeFlowDetails({
        step: 4,
        component: 'SubInit',
      });
    },

    startResendPinCodeTimer() {
      this.showResendPinCode = false;
      this.showResendPinCodeTimer = true;
      this.resendPinCodeInterval = setInterval(() => {
        this.resendPinCodeTimer--;
        if (this.resendPinCodeTimer == 0) {
          this.resetResendPinCodeTimer();
        }
      }, 1000);
    },

    resetResendPinCodeTimer() {
      this.showResendPinCode = true;
      this.showResendPinCodeTimer = false;
      this.resendPinCodeTimer = RESENDINTERVALTIME;
      clearInterval(this.resendPinCodeInterval);
    },
  },
};
</script>

<style lang="scss">
.pinCodeInput input[type='text'] {
  transition: color 900000s, background-color 900000s;
}
</style>
